import { useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectClient, selectMaybeClientPartner } from '@client/model/selectors';
import { mapMaritalStatusToTraditionalLTC } from '@/features/shop-insurance';
import {
  isTraditionalLTCQuote,
  type GuidedPlanningQuote,
} from '@/features/guided-financial-planning/hooks/use-quotes-display';

export type CoverageType = 'single' | 'couples' | 'joint';

interface CoverageTab {
  value: CoverageType;
  label: string;
}

function matchesCoverageType(quote: GuidedPlanningQuote, coverageType: CoverageType): boolean {
  if (isTraditionalLTCQuote(quote)) {
    if (coverageType === 'single') return quote.maritalClass === 'Single';
    if (coverageType === 'couples')
      return quote.maritalClass === 'Married 1 Buy' || quote.maritalClass === 'Married 2 Buy';
    return false;
  }

  const policyQuote = quote as PolicyQuote;
  if (coverageType === 'joint') return policyQuote.policyType === 'Joint Life';
  if (coverageType === 'couples') return policyQuote.policyType === 'Couples Discount';
  return policyQuote.policyType === 'Single Life' || policyQuote.policyType === 'Standard';
}

export function useCoverageTypeToggle() {
  const client = useSelector(selectClient);
  const clientPartner = useSelector(selectMaybeClientPartner);

  const hasPartnerWithDemographics = useMemo(() => {
    return Boolean(
      client?.partnerClientId &&
      clientPartner?.intakeSurvey?.clientBirthDate &&
      clientPartner?.intakeSurvey?.clientGenderAtBirth,
    );
  }, [
    client?.partnerClientId,
    clientPartner?.intakeSurvey?.clientBirthDate,
    clientPartner?.intakeSurvey?.clientGenderAtBirth,
  ]);

  const isMarriedOrPartnered = useMemo(() => {
    return (
      mapMaritalStatusToTraditionalLTC(client?.intakeSurvey?.clientMaritalStatus) === 'Married' ||
      hasPartnerWithDemographics
    );
  }, [client?.intakeSurvey?.clientMaritalStatus, hasPartnerWithDemographics]);

  const [selectedCoverageType, setSelectedCoverageType] = useState<CoverageType>(
    hasPartnerWithDemographics ? 'joint' : 'single',
  );
  const [showPartnerRequiredDialog, setShowPartnerRequiredDialog] = useState(false);

  const availableTabs = useMemo((): CoverageTab[] => {
    const tabs: CoverageTab[] = [{ value: 'single', label: 'Single coverage' }];

    if (isMarriedOrPartnered) {
      tabs.push({ value: 'couples', label: 'Married discount' });
      tabs.push({ value: 'joint', label: 'Joint coverage' });
    }

    return tabs;
  }, [isMarriedOrPartnered]);

  const handleCoverageTypeChange = useCallback(
    (tab: string) => {
      const tabValue = tab as CoverageType;
      if (tabValue === 'joint' && !hasPartnerWithDemographics) {
        setShowPartnerRequiredDialog(true);
        return;
      }
      setSelectedCoverageType(tabValue);
    },
    [hasPartnerWithDemographics],
  );

  const filterQuotesByCoverageType = useCallback(
    (quotes: GuidedPlanningQuote[] | undefined): GuidedPlanningQuote[] | undefined => {
      if (!quotes || !isMarriedOrPartnered) return quotes;
      return quotes.filter(q => matchesCoverageType(q, selectedCoverageType));
    },
    [selectedCoverageType, isMarriedOrPartnered],
  );

  return {
    selectedCoverageType,
    setSelectedCoverageType,
    handleCoverageTypeChange,
    isMarriedOrPartnered,
    hasPartnerWithDemographics,
    showPartnerRequiredDialog,
    setShowPartnerRequiredDialog,
    availableTabs,
    filterQuotesByCoverageType,
  };
}
